import { useStore } from '@nanostores/react'
import { getLinkedInId, getLinkedInLinkProps } from 'features/contacts/utils'
import { ExecutionsUI } from 'features/executions'
import { memo } from 'react'
import { appDay } from 'shared/day'
import { CellTooltip, ContactsTable } from 'shared/ui'

import type { Contact } from '../../types'

import {
	$contactColumns,
	$executionsColumns,
	Column
} from '../../utils/columns'

type KeyMap = Partial<Record<Column, keyof Contact>>

const columnToDateKey = {
	[Column.Added]: 'created',
	[Column.Imported]: 'imported',
	[Column.LastAction]: 'contacted'
} as const satisfies KeyMap

const columnToKey = {
	[Column.Custom1]: 'custom_field_1',
	[Column.Custom2]: 'custom_field_2',
	[Column.Custom3]: 'custom_field_3',
	[Column.Headline]: 'occupation',
	[Column.Location]: 'location'
} as const satisfies KeyMap

type DateKey = keyof typeof columnToDateKey
type InfoItem = Partial<Pick<Contact, (typeof columnToDateKey)[DateKey]>> &
	Partial<Pick<Contact, (typeof columnToKey)[PropKey]>> &
	Pick<
		Contact,
		| 'distance'
		| 'full_name'
		| 'is_premium'
		| 'linkedin_entity_id'
		| 'linkedin_public_identifier'
		| 'picture_url'
		| 'last_job'
	>

type PropKey = keyof typeof columnToKey
export function ContactInfoCells({
	column,
	...contact
}: InfoItem & { column: Column }) {
	if (column === Column.Contact) {
		return (
			<ContactsTable.NameCell
				avatar={contact.picture_url}
				distance={contact.distance}
				fullName={contact.full_name ?? contact.linkedin_public_identifier}
				isPremium={contact.is_premium}
				linkedinId={getLinkedInId(contact)}
			/>
		)
	}

	if (column === Column.PositionTitle) {
		const title = contact.last_job?.title ?? ''
		return (
			<CellTooltip className={ContactsTable.css.cell} content={title}>
				{title}
			</CellTooltip>
		)
	}

	if (column === Column.CompanyName) {
		const company = contact.last_job?.company?.name ?? ''
		return (
			<CellTooltip className={ContactsTable.css.cell} content={company}>
				{company}
			</CellTooltip>
		)
	}

	if (column === Column.Location) {
		const key = columnToKey[column]
		const value = contact[key]
		return (
			<CellTooltip className={ContactsTable.css.cell} content={value || ''}>
				{value}
			</CellTooltip>
		)
	}

	let text = null

	if (column in columnToDateKey) {
		const key = columnToDateKey[column as DateKey]
		const value = contact[key]

		text = value && appDay(value).format('DD MMM YYYY')
	}

	if (column in columnToKey) {
		const key = columnToKey[column as PropKey]
		text = contact[key] ?? text
	}

	return (
		<CellTooltip className={ContactsTable.css.cell} content={text || ''}>
			{text}
		</CellTooltip>
	)
}

function InfoCells_(contact: Contact) {
	const columns = useStore($contactColumns)

	return columns.map(column => (
		<ContactInfoCells column={column} key={column} {...contact} />
	))
}

const ContactsActions = ({
	id,
	...contact
}: Pick<
	Contact,
	'actions' | 'id' | 'linkedin_entity_id' | 'linkedin_public_identifier'
>) => (
	<td className={ContactsTable.css.actions}>
		<div className={ContactsTable.css.actionsWrap}>
			<ExecutionsUI.Actions.Executions id={id} key='ℹ️' />
			<ExecutionsUI.Actions.LinkedLink {...getLinkedInLinkProps(contact)} />
		</div>
	</td>
)

const CampaignActions = ({
	...contact
}: Pick<
	Contact,
	'actions' | 'linkedin_entity_id' | 'linkedin_public_identifier'
>) => (
	<td className={ContactsTable.css.actions}>
		<div className={ContactsTable.css.actionsWrap}>
			<ExecutionsUI.Actions.LinkedLink {...getLinkedInLinkProps(contact)} />
		</div>
	</td>
)

const ContactsRow_ = (contact: Contact) => [
	<InfoCells key='info' {...contact} />,
	<ContactsActions key='actions' {...contact} />
]
const CampaignContactsRow_ = (contact: Contact) => {
	const columns = useStore($executionsColumns)

	return [
		...columns.map(key => (
			<ContactInfoCells column={key} key={key} {...contact} />
		)),
		<CampaignActions key='actions' {...contact} />
	]
}

export const ContactsRow = memo(ContactsRow_)
export const CampaignContactsRow = memo(CampaignContactsRow_)
export const InfoCells = memo(InfoCells_)
